import { useId, useState, type ReactElement } from 'react';
import { ChevronIcon } from '@/components/catalog/icons';
import { Badge, Button, Skeleton } from '@/components/ui';
import { TextLink } from '@/components/ui/TextLink';
import { formatDateOnly } from '@/lib/format';
import { BarList } from '../fab-ui';
import { piCount } from '../pi-kit';
import {
  COMPETITION_LABEL,
  editionHeadcount,
  type ChallengeEditionView,
  type ChallengePartnerSchool,
} from './challenges-model';

/**
 * One edition of a SEED challenge, as its partner schools would read it.
 * Cards sit in an equal-height grid: header (title, competition, date), the
 * four key numbers, partner schools, later outcomes, and an inline
 * "Participant background" disclosure (top UG institutions, intended PG start)
 * pinned to the bottom of the card.
 *
 * - A live edition (`source: 'comms_form'`, read from the registration form)
 *   shows a "Registrations open" badge while `registrationsOpen` is true, and
 *   its Registrations count in place of Participants (`editionHeadcount`).
 * - Teams / finalists / winners are null until collected and render an em
 *   dash, never 0.
 * - The upstream sends no close date through EMERGE, so no "Closes …" fact.
 * - "Later" outcomes are correlation, not attribution (see the model header).
 * - A partner school links to `/companies/:id` only through the server's
 *   `company` enrichment.
 */

const UG_MAX_ROWS = 8;

function editionDateText(edition: ChallengeEditionView): string | null {
  if (edition.editionDate !== null) return formatDateOnly(edition.editionDate);
  return edition.editionYear === null ? null : String(edition.editionYear);
}

function PartnerSchool({ school }: { school: ChallengePartnerSchool }) {
  return (
    <li className="min-w-0">
      {school.company !== null ? (
        <TextLink to={`/companies/${school.company.id}`} className="[overflow-wrap:anywhere]">
          {school.company.name}
        </TextLink>
      ) : (
        <span className="text-fg [overflow-wrap:anywhere]">{school.schoolName}</span>
      )}
      {school.universityName !== null &&
        school.universityName !== (school.company?.name ?? school.schoolName) && (
          <span className="text-xs text-fg-muted"> · {school.universityName}</span>
        )}
    </li>
  );
}

function KeyFigure({ label, value }: { label: string; value: number | null }) {
  return (
    <div className="min-w-0">
      <dt className="truncate text-xs text-fg-muted">{label}</dt>
      <dd className="mt-0.5 text-xl font-semibold text-fg tabular-nums">{piCount(value)}</dd>
    </div>
  );
}

function OutcomeFigure({
  label,
  all,
  partner,
}: {
  label: string;
  all: number | null;
  partner: number | null;
}) {
  return (
    <div className="min-w-0">
      <dt className="truncate text-xs text-fg-muted">{label}</dt>
      <dd className="mt-0.5 grid min-w-0">
        <span className="text-sm font-semibold text-fg tabular-nums">{piCount(all)}</span>
        <span className="truncate text-xs text-fg-muted tabular-nums">
          {piCount(partner)} at partner
        </span>
      </dd>
    </div>
  );
}

function SectionLabel({ children }: { children: string }) {
  return <h3 className="text-xs font-medium text-fg-muted">{children}</h3>;
}

export function ChallengeEditionCard({ edition }: { edition: ChallengeEditionView }): ReactElement {
  const titleId = useId();
  const detailId = useId();
  const [expanded, setExpanded] = useState(false);
  const dateText = editionDateText(edition);
  const headcount = editionHeadcount(edition);
  const { downstream } = edition;

  return (
    <article
      aria-labelledby={titleId}
      className="flex h-full min-w-0 flex-col gap-4 rounded-lg border border-border bg-surface p-4"
    >
      <header className="grid min-w-0 gap-1.5">
        <div className="flex min-w-0 items-baseline justify-between gap-3">
          <h3
            id={titleId}
            className="min-w-0 text-base font-semibold text-fg [overflow-wrap:anywhere]"
          >
            {edition.title}
          </h3>
          {dateText !== null && (
            <span className="shrink-0 text-sm whitespace-nowrap text-fg-muted tabular-nums">
              {dateText}
            </span>
          )}
        </div>
        {(edition.competition !== null || edition.registrationsOpen === true) && (
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            {edition.competition !== null && (
              <Badge tone="neutral" variant="outline">
                {COMPETITION_LABEL[edition.competition]}
              </Badge>
            )}
            {edition.registrationsOpen === true && <Badge tone="success">Registrations open</Badge>}
          </div>
        )}
      </header>

      <dl className="grid grid-cols-2 gap-3 rounded-lg border border-hairline bg-surface-sunken px-3 py-3 sm:grid-cols-4">
        <KeyFigure label={headcount.label} value={headcount.value} />
        <KeyFigure label="Teams" value={edition.teams} />
        <KeyFigure label="Finalists" value={edition.finalists} />
        <KeyFigure label="Winners" value={edition.winners} />
      </dl>

      <div className="grid min-w-0 gap-1">
        <SectionLabel>Partner schools</SectionLabel>
        {edition.partnerSchools.length === 0 ? (
          <p className="text-sm text-fg-muted">—</p>
        ) : (
          <ul className="flex flex-col gap-1 text-sm">
            {edition.partnerSchools.map((school) => (
              <PartnerSchool key={school.key} school={school} />
            ))}
          </ul>
        )}
      </div>

      <div className="grid min-w-0 gap-1.5">
        <SectionLabel>Later outcomes</SectionLabel>
        <dl className="grid grid-cols-3 gap-3">
          <OutcomeFigure label="Applied" all={downstream.applied} partner={downstream.appliedAtPartner} />
          <OutcomeFigure
            label="Admitted"
            all={downstream.admitted}
            partner={downstream.admittedAtPartner}
          />
          <OutcomeFigure
            label="Enrolled"
            all={downstream.enrolled}
            partner={downstream.enrolledAtPartner}
          />
        </dl>
      </div>

      <div className="mt-auto grid min-w-0 gap-3 border-t border-hairline pt-3">
        <div>
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={expanded}
            aria-controls={detailId}
            onClick={() => setExpanded((open) => !open)}
            trailingIcon={<ChevronIcon direction={expanded ? 'up' : 'down'} />}
          >
            Participant background
          </Button>
        </div>
        {expanded && (
          <div id={detailId} className="grid min-w-0 gap-3">
            <BarList
              title="Top UG institutions"
              rows={edition.ugInstitutions}
              maxRows={UG_MAX_ROWS}
              emptyText="No institutions reported."
            />
            <BarList
              title="Intended PG start"
              rows={edition.pgStartYears}
              emptyText="No start years reported."
            />
          </div>
        )}
      </div>
    </article>
  );
}

function CardSkeleton() {
  return (
    <div className="flex flex-col gap-4 rounded-lg border border-border bg-surface p-4">
      <div className="flex justify-between gap-3">
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-4 w-20" />
      </div>
      <Skeleton className="h-5 w-28" />
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-4 w-56" />
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-8 w-44" />
    </div>
  );
}

/** Two card-shaped placeholders in the same grid as the cards. */
export function ChallengeEditionCardSkeleton(): ReactElement {
  return (
    <div aria-hidden="true" className="grid min-w-0 gap-4 xl:grid-cols-2">
      <CardSkeleton />
      <CardSkeleton />
    </div>
  );
}
